import React, { useRef, useEffect, useCallback } from 'react';
import { select, group, drag, zoom, zoomIdentity, ZoomBehavior } from 'd3';
import {
  ROWS, SUBGROUPS, ROW_COLORS, NODES, EDGES,
  MacroNode, MacroEdge,
} from '../../data/macroData';

interface MacroGraphProps {
  activeEdges: Set<string>;
  activeNodes: Set<string>;
  onNodeClick: (node: MacroNode) => void;
  onEdgeClick: (edge: MacroEdge) => void;
  onBackgroundClick: () => void;
}

interface PositionedNode extends MacroNode {
  x: number;
  y: number;
}

const MacroGraph: React.FC<MacroGraphProps> = ({
  activeEdges,
  activeNodes,
  onNodeClick,
  onEdgeClick,
  onBackgroundClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement | null>(null);
  const nodesRef = useRef<PositionedNode[]>([]);
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  // Build graph layout once
  const buildLayout = useCallback((width: number, height: number) => {
    const rowY: Record<string, number> = {};
    const topPad = 55;
    const rowGap = (height - topPad - 60) / (ROWS.length - 1) * 0.55;
    ROWS.forEach((r, i) => { rowY[r.id] = topPad + i * rowGap; });

    const xPos = new Map<string, number>();
    const nodesByRow = group(NODES, (d) => d.row);

    for (const row of ROWS) {
      const list = nodesByRow.get(row.id) || [];
      const subOrder = SUBGROUPS[row.id] || ['Main'];
      const bySub = group(list, (d) => d.subgroup);
      const totalCount = Math.max(list.length, 1);
      const leftPad = 24;
      const rightPad = 24;
      const usableW = width - leftPad - rightPad;
      let cursor = leftPad;

      subOrder.forEach((sub) => {
        const arr = bySub.get(sub) || [];
        const share = Math.max(arr.length / totalCount, 0.10);
        const blockW = usableW * share;
        if (arr.length > 0) {
          arr.forEach((node, i) => {
            const x = cursor + (blockW * (i + 1)) / (arr.length + 1);
            xPos.set(node.id, x);
          });
        }
        cursor += blockW;
      });
    }

    nodesRef.current = NODES.map((n) => ({
      ...n,
      x: xPos.get(n.id) ?? width / 2,
      y: rowY[n.row] ?? height / 2,
    }));

    return { rowY, nodes: nodesRef.current };
  }, []);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const W = rect.width || 900;
    const H = rect.height || 600;

    const svgSel = select(svg);
    svgSel.selectAll('*').remove();
    svgSel.attr('viewBox', `0 0 ${W} ${H}`);

    // Defs — arrow marker
    const defs = svgSel.append('defs');
    defs.append('marker')
      .attr('id', 'arrow')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 18).attr('refY', 0)
      .attr('markerWidth', 6).attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', 'rgba(99,102,241,0.4)');

    const gZoom = svgSel.append('g').attr('class', 'zoom-layer');
    gRef.current = gZoom.node();

    // Zoom
    const zoomBehavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.4, 3])
      .on('zoom', (event) => gZoom.attr('transform', event.transform));
    svgSel.call(zoomBehavior);
    zoomRef.current = zoomBehavior;

    const { rowY, nodes } = buildLayout(W, H);
    const nodeMap = new Map(nodes.map((n) => [n.id, n]));
    const edges = EDGES.filter((e) => nodeMap.has(e.from) && nodeMap.has(e.to));

    // Row guides
    const guides = gZoom.append('g');
    ROWS.forEach((r) => {
      guides.append('line')
        .attr('x1', 0).attr('x2', W)
        .attr('y1', rowY[r.id]).attr('y2', rowY[r.id])
        .attr('stroke', 'rgba(99,102,241,0.08)').attr('stroke-width', 1);
      guides.append('text')
        .attr('x', 12).attr('y', rowY[r.id] - 14)
        .attr('fill', 'rgba(99,102,241,0.3)')
        .attr('font-size', 10)
        .attr('font-family', 'Inter, sans-serif')
        .text(r.title);
    });

    // Links
    const linkSel = gZoom.append('g')
      .selectAll('line')
      .data(edges)
      .enter()
      .append('line')
      .attr('stroke', 'rgba(99,102,241,0.25)')
      .attr('stroke-width', 1.2)
      .attr('marker-end', 'url(#arrow)')
      .attr('x1', (d) => nodeMap.get(d.from)!.x)
      .attr('y1', (d) => nodeMap.get(d.from)!.y)
      .attr('x2', (d) => nodeMap.get(d.to)!.x)
      .attr('y2', (d) => nodeMap.get(d.to)!.y)
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        onEdgeClick(d);
      });

    // Edge labels
    const edgeLabelSel = gZoom.append('g')
      .selectAll('text')
      .data(edges)
      .enter()
      .append('text')
      .attr('fill', 'rgba(165,180,252,0.3)')
      .attr('font-size', 9)
      .attr('font-family', 'JetBrains Mono, monospace')
      .attr('text-anchor', 'middle')
      .attr('x', (d) => (nodeMap.get(d.from)!.x + nodeMap.get(d.to)!.x) / 2)
      .attr('y', (d) => (nodeMap.get(d.from)!.y + nodeMap.get(d.to)!.y) / 2)
      .text((d) => `${d.sign > 0 ? '+' : '−'}${Math.round(d.w * 100)}%`);

    // Update link positions helper
    function updateLinks() {
      linkSel
        .attr('x1', (d) => nodeMap.get(d.from)!.x)
        .attr('y1', (d) => nodeMap.get(d.from)!.y)
        .attr('x2', (d) => nodeMap.get(d.to)!.x)
        .attr('y2', (d) => nodeMap.get(d.to)!.y);
      edgeLabelSel
        .attr('x', (d) => (nodeMap.get(d.from)!.x + nodeMap.get(d.to)!.x) / 2)
        .attr('y', (d) => (nodeMap.get(d.from)!.y + nodeMap.get(d.to)!.y) / 2);
    }

    // Drag
    const dragBehavior = drag<SVGGElement, PositionedNode>()
      .on('start', (event) => event.sourceEvent?.stopPropagation?.())
      .on('drag', function (event, d) {
        d.x = event.x;
        d.y = event.y;
        select(this).attr('transform', `translate(${d.x},${d.y})`);
        updateLinks();
      });

    // Nodes
    const nodeSel = gZoom.append('g')
      .selectAll<SVGGElement, PositionedNode>('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('transform', (d) => `translate(${d.x},${d.y})`)
      .style('cursor', 'pointer')
      .call(dragBehavior)
      .on('click', (event, d) => {
        event.stopPropagation();
        onNodeClick(d);
      });

    nodeSel.append('circle')
      .attr('r', 14)
      .attr('fill', (d) => ROW_COLORS[d.row] || '#6366f1')
      .attr('stroke', 'rgba(7,11,20,0.8)')
      .attr('stroke-width', 2);

    nodeSel.append('text')
      .attr('y', 1)
      .attr('font-size', 13)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .text((d) => d.icon);

    // Labels (two lines max)
    nodeSel.each(function (d) {
      const words = d.label.split(/\s+/);
      const lines: string[] = [];
      let current = '';
      for (const w of words) {
        const test = current ? `${current} ${w}` : w;
        if (test.length <= 12) { current = test; }
        else {
          if (current) lines.push(current);
          current = w.length > 12 ? w.slice(0, 11) + '…' : w;
          if (lines.length >= 1) break;
        }
      }
      if (current) lines.push(current);

      const textEl = select(this).append('text')
        .attr('text-anchor', 'middle')
        .attr('y', -24)
        .attr('fill', '#a5b4fc')
        .attr('font-size', 10)
        .attr('font-family', 'Inter, sans-serif');

      lines.forEach((ln, i) => {
        textEl.append('tspan').attr('x', 0).attr('dy', i === 0 ? 0 : 12).text(ln);
      });
    });

    // Background click
    svgSel.on('click', () => onBackgroundClick());

    // Store selections for highlight updates
    (svg as any).__linkSel = linkSel;
    (svg as any).__nodeSel = nodeSel;
    (svg as any).__edgeLabelSel = edgeLabelSel;

    // Fit button handler
    (svg as any).__fitToScreen = () => {
      svgSel.transition().duration(250).call(zoomBehavior.transform, zoomIdentity);
    };
  }, [buildLayout, onNodeClick, onEdgeClick, onBackgroundClick]);

  // Highlight update
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const linkSel = (svg as any).__linkSel;
    const nodeSel = (svg as any).__nodeSel;
    const edgeLabelSel = (svg as any).__edgeLabelSel;
    if (!linkSel || !nodeSel || !edgeLabelSel) return;

    const hasHighlight = activeEdges.size > 0 || activeNodes.size > 0;

    linkSel
      .attr('stroke', (d: MacroEdge) => activeEdges.has(d.id) ? '#e0e7ff' : 'rgba(99,102,241,0.25)')
      .attr('stroke-width', (d: MacroEdge) => activeEdges.has(d.id) ? 2.5 : 1.2)
      .attr('opacity', (d: MacroEdge) => hasHighlight && !activeEdges.has(d.id) ? 0.1 : 1);

    nodeSel
      .attr('opacity', (d: PositionedNode) => hasHighlight && !activeNodes.has(d.id) ? 0.2 : 1);

    nodeSel.selectAll('circle')
      .attr('stroke', (d: PositionedNode) => activeNodes.has(d.id) ? '#e0e7ff' : 'rgba(7,11,20,0.8)')
      .attr('stroke-width', (d: PositionedNode) => activeNodes.has(d.id) ? 3 : 2);

    edgeLabelSel
      .attr('opacity', (d: MacroEdge) => {
        if (!hasHighlight) return 0.3;
        return activeEdges.has(d.id) ? 0.95 : 0.05;
      });
  }, [activeEdges, activeNodes]);

  return (
    <svg
      ref={svgRef}
      className="macro-graph-svg w-full h-full block"
      style={{ background: '#070b14' }}
    />
  );
};

export default MacroGraph;
